/**
 * Chat composer: D3 chat-lock banner, daily-limit banner, suggestion chips, reference-file chips,
 * attach menu and the input row. The composer is a non-shrinking footer of
 * the chat column, so it stays on screen at every width (CHAT-1).
 *
 * Web: Enter sends, Shift+Enter inserts a newline (CHAT-25).
 */
import React from 'react';
import { ActivityIndicator, ScrollView, TextInput, TouchableOpacity, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { Text } from '@/components/ui/text';
import { reviewDesk } from '@/design-system';
import { useTranslation } from '../../../hooks/useTranslation';
import { webChatStyles } from '../chatStyles';
import { shouldSendOnKey } from '../composerKeys';

export interface ReferenceFileChip {
  message_id: number;
  filename: string;
  char_count: number;
}

export interface ChatComposerProps {
  isPhone: boolean;
  composerRef: React.RefObject<any>;
  inputText: string;
  onChangeText: (value: string) => void;
  onSend: () => void;
  onFocus: () => void;
  placeholder: string;
  /** Input accepts typing. */
  editable: boolean;
  /** Send allowed (besides having text). */
  canSend: boolean;
  busy: boolean;
  attachBusy: boolean;
  dragOver: boolean;
  dailyLimit: { used: number; limit: number } | null;
  /**
   * D3: chat needs a subscription (server `chat` entitlement); null = unlocked.
   * `reassessOnly`: credit-billed re-assessments still work (`chat_reassess`).
   */
  chatLock?: { onSeePlans: () => void; reassessOnly?: boolean } | null;
  suggestedPrompts: string[];
  showSuggestions: boolean;
  referenceFiles: ReferenceFileChip[];
  onRemoveReference: (messageId: number) => void;
  showAttachMenu: boolean;
  onToggleAttachMenu: () => void;
  onPickAnalysisFile: () => void;
  onAttachReference: () => void;
}

export function ChatComposer({
  isPhone,
  composerRef,
  inputText,
  onChangeText,
  onSend,
  onFocus,
  placeholder,
  editable,
  canSend,
  busy,
  attachBusy,
  dragOver,
  dailyLimit,
  chatLock = null,
  suggestedPrompts,
  showSuggestions,
  referenceFiles,
  onRemoveReference,
  showAttachMenu,
  onToggleAttachMenu,
  onPickAnalysisFile,
  onAttachReference,
}: ChatComposerProps) {
  const { t } = useTranslation();
  const sendDisabled = !inputText.trim() || !canSend;

  return (
    <View style={[webChatStyles.composerWrap, isPhone && webChatStyles.composerWrapPhone]}>
      {chatLock && (
        <View style={webChatStyles.limitBanner} testID="chat_locked_banner">
          <MaterialIcons name="lock" size={20} color="#A86505" />
          <View style={{ flex: 1 }}>
            <Text style={webChatStyles.limitBannerText}>{t('entitlementLocks.chatTitle')}</Text>
            <Text style={{ color: '#8A5B09', fontSize: 12, marginTop: 2 }}>
              {t(chatLock.reassessOnly ? 'entitlementLocks.chatReassessBody' : 'entitlementLocks.chatBody')}
            </Text>
          </View>
          <TouchableOpacity
            testID="chat_locked_see_plans"
            accessibilityRole="button"
            onPress={chatLock.onSeePlans}
          >
            <Text style={[webChatStyles.limitBannerText, { flex: 0, textDecorationLine: 'underline' }]}>
              {t('entitlementLocks.seePlans')}
            </Text>
          </TouchableOpacity>
        </View>
      )}
      {dailyLimit && (
        <View style={webChatStyles.limitBanner}>
          <MaterialIcons name="warning" size={20} color="#A86505" />
          <Text style={webChatStyles.limitBannerText}>
            {t('chat.dailyLimitReached')
              .replace('{{used}}', String(dailyLimit.used))
              .replace('{{limit}}', String(dailyLimit.limit))}
          </Text>
        </View>
      )}
      {showSuggestions && (() => {
        const chips = suggestedPrompts.map((prompt) => (
          <TouchableOpacity
            key={prompt}
            style={[webChatStyles.suggestionChip, isPhone && webChatStyles.suggestionChipPhone]}
            onPress={() => onChangeText(prompt)}
            accessibilityRole="button"
          >
            <Text style={[webChatStyles.suggestionChipText, isPhone && webChatStyles.suggestionChipTextPhone]}>
              {prompt}
            </Text>
          </TouchableOpacity>
        ));
        // Phones: one scrolling row instead of three stacked lines, so the
        // thread keeps the height (VIS-CORE-8).
        return isPhone ? (
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            style={{ flexGrow: 0, marginBottom: 10 }}
            contentContainerStyle={[webChatStyles.suggestionStrip, webChatStyles.suggestionStripPhone, { marginBottom: 0 }]}
            keyboardShouldPersistTaps="handled"
            testID="chat_suggestions"
          >
            {chips}
          </ScrollView>
        ) : (
          <View style={webChatStyles.suggestionStrip} testID="chat_suggestions">{chips}</View>
        );
      })()}
      {referenceFiles.length > 0 && (
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, paddingVertical: 8 }} testID="chat_reference_chips">
          {referenceFiles.map((ref) => (
            <View
              key={ref.message_id}
              style={[
                {
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 6,
                  paddingVertical: 6,
                  paddingHorizontal: 10,
                  borderRadius: 14,
                  backgroundColor: reviewDesk.cardMuted,
                  borderWidth: 1,
                  borderColor: reviewDesk.line,
                },
                isPhone && { width: '100%', maxWidth: '100%', minWidth: 0 },
              ]}
            >
              <MaterialIcons name="bookmark" size={14} color={reviewDesk.accent} />
              <Text
                style={[
                  { color: reviewDesk.ink, fontSize: 12, maxWidth: 220 },
                  isPhone && { flex: 1, flexShrink: 1, minWidth: 0, maxWidth: '100%' },
                ]}
                numberOfLines={1}
              >
                {ref.filename}
              </Text>
              <TouchableOpacity
                onPress={() => onRemoveReference(ref.message_id)}
                accessibilityRole="button"
                accessibilityLabel={t('chat.removeReference')}
              >
                <MaterialIcons name="close" size={14} color={reviewDesk.muted} />
              </TouchableOpacity>
            </View>
          ))}
        </View>
      )}
      {showAttachMenu && (
        <View
          style={{
            alignSelf: 'flex-start',
            marginBottom: 6,
            marginLeft: 4,
            backgroundColor: reviewDesk.card,
            borderRadius: 10,
            borderWidth: 1,
            borderColor: reviewDesk.line,
            shadowColor: reviewDesk.shadow,
            shadowOpacity: 0.18,
            shadowRadius: 12,
            shadowOffset: { width: 0, height: 6 },
            paddingVertical: 4,
            minWidth: isPhone ? 0 : 240,
            width: isPhone ? '100%' : undefined,
            maxWidth: isPhone ? '100%' : undefined,
            flexShrink: isPhone ? 1 : undefined,
          }}
        >
          {[
            { key: 'analyze', icon: 'description' as const, label: t('chat.attachAnalyzeOption'), onPress: onPickAnalysisFile },
            { key: 'reference', icon: 'bookmark-border' as const, label: t('chat.attachReferenceOption'), onPress: onAttachReference },
          ].map((option) => (
            <TouchableOpacity
              key={option.key}
              onPress={option.onPress}
              style={{
                flexDirection: 'row',
                flexWrap: isPhone ? 'wrap' : 'nowrap',
                alignItems: 'center',
                gap: 10,
                paddingVertical: 10,
                paddingHorizontal: 12,
              }}
              accessibilityRole="button"
            >
              <MaterialIcons name={option.icon} size={18} color={reviewDesk.accent} />
              <Text style={{ color: reviewDesk.ink, fontSize: 14, flexShrink: isPhone ? 1 : undefined }}>
                {option.label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      )}
      <View
        ref={composerRef}
        style={[
          webChatStyles.composer,
          isPhone && webChatStyles.composerPhone,
          dragOver && {
            borderColor: reviewDesk.accent,
            borderWidth: 2,
            backgroundColor: reviewDesk.accentWash,
          },
        ]}
      >
        {/* Inline attach menu — small paperclip on the left of input */}
        <TouchableOpacity
          onPress={onToggleAttachMenu}
          disabled={busy || attachBusy}
          accessibilityRole="button"
          accessibilityLabel={t('chat.attachMenu')}
          testID="chat_attach_inline"
          style={{
            width: 32,
            height: 32,
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: 6,
            opacity: (busy || attachBusy) ? 0.5 : 1,
          }}
        >
          {attachBusy ? (
            <ActivityIndicator size="small" color={reviewDesk.muted} />
          ) : (
            <MaterialIcons name="attach-file" size={18} color={reviewDesk.muted} />
          )}
        </TouchableOpacity>
        <TextInput
          testID="chat_input"
          nativeID="chat_input"
          style={[
            webChatStyles.composerInput,
            isPhone && webChatStyles.composerInputPhone,
            { userSelect: 'text', WebkitUserSelect: 'text', cursor: 'text' } as any,
            { flex: 1 },
          ]}
          value={inputText}
          onChangeText={onChangeText}
          onFocus={onFocus}
          onKeyPress={(event: any) => {
            if (!shouldSendOnKey(event?.nativeEvent)) return;
            event.preventDefault?.();
            if (!sendDisabled) onSend();
          }}
          placeholder={placeholder}
          placeholderTextColor={reviewDesk.muted}
          multiline
          maxLength={10000}
          editable={editable}
        />
        <TouchableOpacity
          testID="send_button"
          style={[webChatStyles.sendButton, sendDisabled && webChatStyles.sendButtonDisabled]}
          onPress={onSend}
          disabled={sendDisabled}
          accessibilityRole="button"
          accessibilityLabel={t('chat.send')}
        >
          {busy ? (
            <ActivityIndicator size="small" color="white" />
          ) : (
            <MaterialIcons name="arrow-upward" size={20} color="white" />
          )}
        </TouchableOpacity>
      </View>
      <Text style={webChatStyles.disclaimer}>{t('chat.aiDisclaimer')}</Text>
    </View>
  );
}
